import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { pileTiltDegrees } from '#pplx/frontend/apps/ask/shared/pileTilt.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
import React from 'react';
const PLUS = '+';
/**
 * Creates a set of stacked visuals for multiple sources
 * @param visuals Array of React nodes to display in a stacked format
 * @param options Additional options for customization (rightText, maxVisuals)
 * @returns A JSX element with stacked visuals
 */
export const createStackedVisuals = (visuals, options) => {
    const isOverlapping = options?.isOverlapping ?? false;
    const stackDirection = options?.stackDirection ?? 'left';
    const shape = options?.shape ?? 'circle';
    const tilt = (options?.tilt ?? false) && visuals.length > 1;
    const ringWidth = options?.size === 'lg' || options?.size === 'xl' ? 2 : 1;
    // Overlapping styles apply to multi-item piles, or to single items when explicitly requested.
    const hasOverlappingStyles = isOverlapping && (visuals.length > 1 || (options?.alwaysApplyOverlappingStyles ?? false));
    if (!visuals.length) {
        return _jsx(Icon, { icon: $icon('custom-perplexity-sources-active'), size: "small" });
    }
    const maxVisuals = Math.max(1, options?.maxVisuals ?? 3);
    const getMarginLeft = (isFirst) => {
        if (!isOverlapping || isFirst)
            return undefined;
        switch (options?.size) {
            case 'xs':
            case 'sm':
                return '-0.25rem';
            case 'md':
                return '-0.375rem';
            case 'lg':
                return '-0.5rem';
            case 'xl':
                return '-0.625rem';
            default:
                return undefined;
        }
    };
    const getVisualClassName = () => {
        const baseClass = classNames('relative flex items-center justify-center', {
            'rounded-full': shape === 'circle',
            'rounded-none': shape === 'square',
            'size-3.5': options?.size === 'xs',
            'size-4': options?.size === 'sm',
            'size-5': options?.size === 'md',
            'size-6': options?.size === 'lg',
            'size-7': options?.size === 'xl',
        });
        // Squircle piles draw their background and ring inside the visual (see renderVisual).
        if (!hasOverlappingStyles || shape === 'squircle')
            return baseClass;
        return classNames(baseClass, options?.overlappingClassName ?? 'bg-base ring-soft', {
            'ring-1': ringWidth === 1,
            'ring-2': ringWidth === 2,
        });
    };
    // The squircle mask clips box-shadow rings, so an overlapping squircle visual gets its ring
    // from a masked plate inset one ring width outside its box and its background from a masked
    // plate behind it. The ring plate is a scaled copy of the curve rather than a true offset, so
    // the ring runs slightly wider on the diagonals (about 1.2px at 14px); not visible at pile sizes.
    const renderVisual = (visual) => shape === 'squircle' && hasOverlappingStyles ? (_jsxs(_Fragment, { children: [_jsx("span", { className: classNames('squircle absolute bg-[var(--border-soft)]', {
                    '-inset-px': ringWidth === 1,
                    '-inset-0.5': ringWidth === 2,
                }) }), _jsx("span", { className: "squircle bg-base relative flex size-full items-center justify-center", children: visual })] })) : (visual);
    // Determine how many individual visuals to show and whether to show a remainder count
    // Special case: when maxVisuals is 1 and there are multiple visuals, show only the remainder count
    // Otherwise: show visuals up to the limit, leaving room for remainder count if needed
    const shouldShowRemainder = (options?.showRemainderCount ?? true) && visuals.length > maxVisuals;
    const visualsToShow = maxVisuals === 1 && visuals.length > 1 && shouldShowRemainder
        ? 0 // Show only remainder count when maxVisuals=1 and multiple visuals exist AND remainder should be shown
        : shouldShowRemainder
            ? maxVisuals - 1 // Leave room for remainder count
            : Math.min(maxVisuals, visuals.length); // Show all available visuals up to limit
    const stackedVisuals = visuals.slice(0, visualsToShow).map((visual, index) => (_jsx("div", { className: getVisualClassName(), style: {
            zIndex: stackDirection === 'left' ? visuals.length - index - 1 : visuals.length + index,
            marginLeft: getMarginLeft(index === 0),
            rotate: tilt ? `${pileTiltDegrees(index)}deg` : undefined,
        }, children: renderVisual(visual) }, index)));
    return (_jsxs("div", { className: classNames('isolate', options?.containerClass ?? 'my-1 flex items-center'), children: [_jsxs("div", { className: options?.innerClassName ??
                    classNames('flex items-center', {
                        // If icons are not overlapping, add a 2px gap between them
                        'gap-2': !isOverlapping,
                    }), children: [stackedVisuals, shouldShowRemainder && (_jsx("div", { className: classNames(getVisualClassName(), {
                            'mx-1': visualsToShow === 0,
                        }), style: {
                            zIndex: stackDirection === 'left' ? -1 : visuals.length + maxVisuals,
                            marginLeft: getMarginLeft(visualsToShow === 0),
                            rotate: tilt ? `${pileTiltDegrees(visualsToShow)}deg` : undefined,
                        }, children: renderVisual(_jsxs(TextPrimitive, { variant: "tinyMono", color: "light", as: "span", children: [PLUS, visuals.length - visualsToShow] })) }))] }), options?.rightText && _jsx("div", { className: "ml-2", children: options.rightText })] }));
};
//# sourceMappingURL=createStackedVisuals.js.map